import { isEmpty, upperCase } from 'lodash';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  axiosExternalInstance,
  axiosInstance,
} from '@config/axios/axios.config';

import { paths } from '@common/types';
import { ICareNotesTableFilters } from '@views/pages/Patient/components/CareNotes/hooks/useCareNotesTable.ts';
import { IDocumentsTableFilters } from '@views/pages/Patient/components/Documents/hooks';
import axios from 'axios';
import { stableKey } from '@common/cache/util.ts';

const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

interface IPatientsListProps {
  search: string;
  page: number;
  limit: number;
  sort_order?: string;
  sort_by?: string;
  account_status?: string;
  insurance_type?: string;
  phone_number?: string;
  program_name?: string;
  program_status?: string;
  program_tier?: string;
  birthdate?: string;
  wellness_coordinator_ids?: string[];
  care_manager_ids?: string[];
  care_navigator_ids?: string[];
  practice_ids?: string[];
  provider_ids?: string[];
  partner_id?: string;
  building_ids?: string[];
  my_patients?: boolean;
  recently_viewed?: boolean;
}

const patientsListRequest = async (props: IPatientsListProps) => {
  const response = axiosInstance.get<
    paths[`/record/patients`]['get']['responses']['200']['content']['application/json']
  >(`/record/patients`, {
    params: {
      page: props.page,
      limit: props.limit,
      sort_by: props.sort_by,
      ...(props.sort_order && { sort_order: upperCase(props.sort_order) }),
      ...(props.search && { search: props.search }),
      ...(props.my_patients && { my_patients: props.my_patients }),
      ...(props.recently_viewed && { recently_viewed: props.recently_viewed }),
      ...(props.account_status && { account_status: props.account_status }),
      ...(props.program_status &&
        props.program_status !== 'all_aligned_statuses' && {
          program_status: props.program_status,
        }),
      ...(props.insurance_type && { insurance_type: props.insurance_type }),
      ...(props.program_name && { program_name: props.program_name }),
      ...(props.program_tier && { program_tier: props.program_tier }),
      //TODO: partner_id - Likely will be turned into a proper multi select in the future,
      // so not a bug that it is a single select for now
      ...(props.partner_id && { partner_ids: [props.partner_id] }),
      ...(props.practice_ids &&
        !isEmpty(props.practice_ids) && { practice_ids: props.practice_ids }),
      ...(props.provider_ids &&
        !isEmpty(props.provider_ids) && { provider_ids: props.provider_ids }),
      ...(props.building_ids &&
        !isEmpty(props.building_ids) && { building_ids: props.building_ids }),
      ...(props.care_manager_ids &&
        !isEmpty(props.care_manager_ids) && {
          care_manager_ids: props.care_manager_ids,
        }),
      ...(props.care_navigator_ids &&
        !isEmpty(props.care_navigator_ids) && {
          care_navigator_ids: props.care_navigator_ids,
        }),
      ...(props.wellness_coordinator_ids &&
        !isEmpty(props.wellness_coordinator_ids) && {
          wellness_coordinator_ids: props.wellness_coordinator_ids,
        }),
      ...(props.program_status === 'all_aligned_statuses' && {
        all_aligned_statuses: true,
      }),
      ...(props.birthdate && {
        birthdate: props.birthdate,
      }),
      ...(props.phone_number && { phone_number: props.phone_number }),
      timezone,
    },
  });
  const data = (await response).data;

  return data;
};

export const usePatientsListQuery = (
  props: IPatientsListProps & { additionalFilters: string; enabled?: boolean },
) => {
  const stableProps = stableKey({
    additionalFilters: props.additionalFilters,
    search: props.search,
    partner_id: props.partner_id,
    my_patients: props.my_patients,
    limit: props.limit,
    page: props.page,
    sort_by: props.sort_by,
    sort_order: props.sort_order,
  });

  const query = useQuery({
    queryKey: ['patients', stableProps],
    queryFn: () => patientsListRequest(props),
    enabled: props.enabled ?? true,
    placeholderData: keepPreviousData,
  });

  return query;
};

const recentlyViewedPatientsRequest = async () => {
  const response = axiosInstance.get<
    paths[`/patient/recently-viewed`]['get']['responses']['200']['content']['application/json']
  >(`/patient/recently-viewed`);
  const data = (await response).data;

  return data;
};

export const useRecentlyViewedPatientsQuery = () => {
  const query = useQuery({
    queryKey: ['recentlyViewedPatients'],
    queryFn: () => recentlyViewedPatientsRequest(),
  });

  return query;
};

interface IPatientProps {
  patient_id: string;
}

const patientRequest = async (props: IPatientProps) => {
  const response = axiosInstance.get<
    paths[`/patient/{patient_id}`]['get']['responses']['200']['content']['application/json']
  >(`/patient/${props.patient_id}`, {
    params: {
      timezone,
    },
  });
  const data = (await response).data;

  return data;
};

export const usePatientQuery = (props: IPatientProps) => {
  const query = useQuery({
    queryKey: ['patient', props.patient_id],
    enabled: !!props.patient_id,
    queryFn: () => patientRequest(props),
  });

  return query;
};

interface IPatientProfileProps {
  patient_id: string;
}

const patientProfileRequest = async (props: IPatientProfileProps) => {
  const response = axiosInstance.get<
    paths[`/patient/{patient_id}/profile`]['get']['responses']['200']['content']['application/json']
  >(`/patient/${props.patient_id}/profile`);
  const data = (await response).data;

  return data;
};

export const usePatientProfileQuery = (props: IPatientProfileProps) => {
  const query = useQuery({
    queryKey: ['patientProfile', props.patient_id],
    enabled: !!props.patient_id,
    queryFn: () => patientProfileRequest(props),
  });

  return query;
};

interface IPatientLocationProps {
  patient_id: string;
}

const patientLocationRequest = async (props: IPatientLocationProps) => {
  const response = axiosInstance.get<
    paths[`/patient/{patient_id}/location`]['get']['responses']['200']['content']['application/json']
  >(`/patient/${props.patient_id}/location`);
  const data = (await response).data;

  return data;
};

export const usePatientLocationQuery = (props: IPatientLocationProps) => {
  const query = useQuery({
    queryKey: ['patientLocation', props.patient_id],
    enabled: !!props.patient_id,
    queryFn: () => patientLocationRequest(props),
  });

  return query;
};

interface IPatientContactInformationProps {
  patient_id: string;
}

const patientContactInformationRequest = async (
  props: IPatientContactInformationProps,
) => {
  const response = axiosInstance.get<
    paths[`/patient/{patient_id}/contact-information`]['get']['responses']['200']['content']['application/json']
  >(`/patient/${props.patient_id}/contact-information`);
  const data = (await response).data;

  return data;
};

export const usePatientContactInformationQuery = (
  props: IPatientContactInformationProps,
) => {
  const query = useQuery({
    queryKey: ['patientContactInformation', props.patient_id],
    enabled: !!props.patient_id,
    queryFn: () => patientContactInformationRequest(props),
  });

  return query;
};

interface IPatientInsurancesProps {
  patient_id: string;
}

const patientInsurancesRequest = async (props: IPatientInsurancesProps) => {
  const response = axiosInstance.get<
    paths[`/patient/{patient_id}/insurances`]['get']['responses']['200']['content']['application/json']
  >(`/patient/${props.patient_id}/insurances`);
  const data = (await response).data;

  return data;
};

export const usePatientInsurancesQuery = (props: IPatientInsurancesProps) => {
  const query = useQuery({
    queryKey: ['patient', props.patient_id, 'insurance'],
    enabled: !!props.patient_id,
    queryFn: () => patientInsurancesRequest(props),
  });

  return query;
};

interface IPatientAdditionalInformationProps {
  patient_id: string;
}

const patientAdditionalInformationRequest = async (
  props: IPatientAdditionalInformationProps,
) => {
  const response = axiosInstance.get<
    paths[`/patient/{patient_id}/additional-information`]['get']['responses']['200']['content']['application/json']
  >(`/patient/${props.patient_id}/additional-information`);
  const data = (await response).data;

  return data;
};

export const usePatientAdditionalInformationQuery = (
  props: IPatientAdditionalInformationProps,
) => {
  const query = useQuery({
    queryKey: ['patientAdditionalInformation', props.patient_id],
    enabled: !!props.patient_id,
    queryFn: () => patientAdditionalInformationRequest(props),
  });

  return query;
};

const patientCareTeamRequest = async (patient_id: string) => {
  const response = axiosInstance.get<
    paths[`/patient/{patient_id}/care-team`]['get']['responses']['200']['content']['application/json']
  >(`/patient/${patient_id}/care-team`);
  const data = (await response).data;

  return data;
};

export const usePatientCareTeamQuery = (patient_id: string) => {
  const query = useQuery({
    enabled: !!patient_id,
    queryKey: ['patientCareTeam', patient_id],
    queryFn: () => patientCareTeamRequest(patient_id),
  });

  return query;
};

const patientCareTeamCaregiverRequest = async (patient_id: string) => {
  const response = axiosInstance.get<
    paths[`/patient/{patient_id}/caregiver`]['get']['responses']['200']['content']['application/json']
  >(`/patient/${patient_id}/caregiver`);
  const data = (await response).data;

  return data;
};

export const usePatientCareTeamCaregiverQuery = (patient_id: string) => {
  const query = useQuery({
    enabled: !!patient_id,
    queryKey: ['patientCareTeamCaregiver', patient_id],
    queryFn: () => patientCareTeamCaregiverRequest(patient_id),
  });

  return query;
};

const patientCareTeamEmergencyRequest = async (
  patient_id: string,
  pec_id: string,
) => {
  const response = axiosInstance.get<
    paths[`/patient/{patient_id}/emergency-contact/{pec_id}`]['get']['responses']['200']['content']['application/json']
  >(`/patient/${patient_id}/emergency-contact/${pec_id}`);
  const data = (await response).data;

  return data;
};

export const usePatientCareTeamEmergencyContactQuery = (
  patient_id: string,
  pec_id: string,
) => {
  const query = useQuery({
    enabled: !!pec_id && !!patient_id,
    queryKey: ['patientCareTeamEmergencyContact', patient_id, pec_id],
    queryFn: () => patientCareTeamEmergencyRequest(patient_id, pec_id),
  });

  return query;
};

const patientCarePlanRequest = async (patient_id: string) => {
  const response = axiosInstance.get<
    paths[`/patient/{patient_id}/care-plan`]['get']['responses']['200']['content']['application/json']
  >(`/patient/${patient_id}/care-plan`);
  const data = (await response).data;

  return data;
};

export const usePatientCarePlanQuery = (patient_id: string) => {
  const query = useQuery({
    enabled: !!patient_id,
    queryKey: ['patientCarePlan', patient_id],
    queryFn: () => patientCarePlanRequest(patient_id),
  });

  return query;
};

const focusConditionTemplatesRequest = async () => {
  const response = axiosInstance.get<
    paths[`/patient/care-plans/focus-condition-templates`]['get']['responses']['200']['content']['application/json']
  >(`/patient/care-plans/focus-condition-templates`);
  const data = (await response).data;

  return data;
};

export const useFocusConditionTemplatesQuery = () => {
  const query = useQuery({
    queryKey: ['focusConditionTemplates'],
    queryFn: focusConditionTemplatesRequest,
  });

  return query;
};

const patientCareNotesRequest = async (
  patient_id: string,
  props: ICareNotesTableFilters,
) => {
  const response = axiosInstance.get<
    paths[`/patient/{patient_id}/care-notes`]['get']['responses']['200']['content']['application/json']
  >(`/patient/${patient_id}/care-notes`, {
    params: {
      page: props.page,
      limit: props.limit,
      sort_by: props.sort_by,
      ...(props.sort_order && { sort_order: upperCase(props.sort_order) }),
      ...(props.user_id && { user_id: props.user_id }),
      ...(props.note_type && { note_type: props.note_type }),
      ...(props.program_id && { program_id: props.program_id }),
      ...(props.provider_id && { provider_id: props.provider_id }),
    },
  });
  const data = (await response).data;

  return data;
};

export const usePatientCareNotesQuery = (
  patient_id: string,
  props: ICareNotesTableFilters,
) => {
  const query = useQuery({
    enabled: !!patient_id,
    queryKey: ['patientCareNotes', patient_id, stableKey(props)],
    queryFn: () => patientCareNotesRequest(patient_id, props),
  });

  return query;
};

const patientCareNoteRequest = async (
  patient_id: string,
  care_note_id: string,
) => {
  const response = axiosInstance.get<
    paths[`/patient/{patient_id}/care-note/{care_note_id}`]['get']['responses']['200']['content']['application/json']
  >(`/patient/${patient_id}/care-note/${care_note_id}`);
  const data = (await response).data;

  return data;
};

export const usePatientCareNoteQuery = (
  patient_id: string,
  care_note_id: string,
) => {
  const query = useQuery({
    enabled: !!patient_id && !!care_note_id,
    queryKey: ['patientCareNote', patient_id, care_note_id],
    queryFn: () => patientCareNoteRequest(patient_id, care_note_id),
  });

  return query;
};

const patientHistoryRequest = async (props: {
  patient_id: string;
  limit: number;
  page: number;
}) => {
  const response = axiosInstance.get<
    paths[`/patient/{patient_id}/history`]['get']['responses']['200']['content']['application/json']
  >(`/patient/${props.patient_id}/history`, {
    params: {
      page: props.page,
      limit: props.limit,
    },
  });
  const data = (await response).data;

  return data;
};

export const usePatientHistoryQuery = (props: {
  patient_id: string;
  limit: number;
  page: number;
  enabled: boolean;
}) => {
  const query = useQuery({
    enabled: !!props.enabled,
    queryKey: ['patient', 'history', stableKey(props)],
    queryFn: () => patientHistoryRequest(props),
  });

  return query;
};

const patientDocumentsRequest = async (
  patient_id: string,
  props: IDocumentsTableFilters,
) => {
  const response = axiosInstance.get<
    paths[`/patient/{patient_id}/documents`]['get']['responses']['200']['content']['application/json']
  >(`/patient/${patient_id}/documents`, {
    params: {
      ...(props?.category && { category: props.category }),
      ...(props?.page && { page: props.page }),
      ...(props?.limit && { limit: props.limit }),
      ...(props?.sort_by && { sort_by: props.sort_by }),
      ...(props?.sort_order && {
        ...(props.sort_order && { sort_order: upperCase(props.sort_order) }),
      }),
      ...(props?.search && { search: props.search }),
    },
  });
  const data = (await response).data;

  return data;
};

export const usePatientDocumentsQuery = (
  patient_id: string,
  props: IDocumentsTableFilters,
) => {
  const query = useQuery({
    enabled: !!patient_id,
    queryKey: ['patientDocuments', patient_id, stableKey(props)],
    queryFn: () => patientDocumentsRequest(patient_id, props),
  });

  return query;
};

interface IPatientsShortQueryProps {
  search?: string;
}

const patientsShortRequest = async ({ search }: IPatientsShortQueryProps) => {
  const response = axiosInstance.get<
    paths[`/record/patients/short`]['get']['responses']['200']['content']['application/json']
  >(`/record/patients/short`, { params: { search } });
  const data = (await response).data;
  return data;
};

export const usePatientsShortQuery = ({ search }: IPatientsShortQueryProps) => {
  const orgQuery = useQuery({
    enabled: !!(search && search.length > 2),
    queryKey: ['patientsShort', search],
    queryFn: () => patientsShortRequest({ search }),
  });

  return orgQuery;
};

interface IPatientLogoProps {
  patient_id: string;
}

const patientLogoRequest = async (
  props: IPatientLogoProps,
  signal?: AbortSignal,
): Promise<Blob | null> => {
  try {
    const response = await axiosInstance.get<Blob>(
      `/patient/${props.patient_id}/avatar.webp`,
      {
        headers: { 'Content-type': 'image/webp' },
        responseType: 'blob',
        signal,
        skipNotFoundRedirect: true,
      },
    );
    return response.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) return null;
    throw err;
  }
};

export const usePatientLogoQuery = (props: IPatientLogoProps) => {
  const query = useQuery({
    queryKey: ['patient', props.patient_id, 'logo'],
    queryFn: () => patientLogoRequest(props),
  });

  return query;
};

const getPatientMetadataRequest = async () => {
  const response = axiosInstance.get<
    paths[`/patient/care-plans/metadata`]['get']['responses']['200']['content']['application/json']
  >(`/patient/care-plans/metadata`);
  const data = (await response).data;

  return data;
};

export const usePatientMetaDataQuery = () => {
  const query = useQuery({
    queryKey: ['patient', 'metadata'],
    queryFn: () => getPatientMetadataRequest(),
  });

  return query;
};

const patientsMetadataRequest = async () => {
  const response = axiosInstance.get<
    paths[`/record/patients/filter-metadata`]['get']['responses']['200']['content']['application/json']
  >(`/record/patients/filter-metadata`, {
    params: {
      timezone,
    },
  });
  const data = (await response).data;

  return data;
};

export const usePatientsMetadataQuery = () => {
  const query = useQuery({
    queryKey: ['patientsMetadata'],
    queryFn: () => patientsMetadataRequest(),
  });

  return query;
};

interface IRespitePartnersShortProps {
  patient_id: string;
  search?: string;
}

const respitePartnersRequest = async ({
  patient_id,
  search,
}: IRespitePartnersShortProps) => {
  const response = axiosInstance.get<
    paths[`/patient/{patient_id}/respite-partners`]['get']['responses']['200']['content']['application/json']
  >(`/patient/${patient_id}/respite-partners`, {
    params: {
      ...(search && { search }),
    },
  });
  const data = (await response).data;

  return data;
};

export const useRespitePartnersShortQuery = (
  props: IRespitePartnersShortProps,
) => {
  const query = useQuery({
    // enabled: !!(props.search && props.search.length > 2),
    queryKey: ['respitePartnersShort', props.patient_id, props.search],
    queryFn: () => respitePartnersRequest(props),
  });

  return query;
};

interface IAssessmentsListProps {
  page: number;
  limit: number;
  sort_order?: string;
  sort_by?: string;
  date?: string;
  type?: string;
  completed?: boolean;
  patient_id: string;
}

const assessmentsListRequest = async (props: IAssessmentsListProps) => {
  const response = axiosInstance.get<
    paths[`/patient/{patient_id}/assessments`]['get']['responses']['200']['content']['application/json']
  >(`/patient/${props.patient_id}/assessments`, {
    params: {
      page: props.page,
      limit: props.limit,
      sort_by: props.sort_by,
      ...(props.sort_order && { sort_order: upperCase(props.sort_order) }),
      ...(props.type && { type: props.type }),
      ...(props.completed !== undefined && { completed: props.completed }),
      ...(props.date && { date: props.date }),
    },
  });
  const data = (await response).data;

  return data;
};

export const useAssessmentsListQuery = (props: IAssessmentsListProps) => {
  const query = useQuery({
    enabled: !!props.patient_id,
    queryKey: ['assessments', stableKey(props)],
    queryFn: () => assessmentsListRequest(props),
  });

  return query;
};

interface IAssessmentProps {
  patient_id: string;
  assessment_id: string;
}

const assessmentRequest = async (props: IAssessmentProps) => {
  const response = axiosInstance.get<
    paths[`/patient/{patient_id}/assessment/{assessment_id}`]['get']['responses']['200']['content']['application/json']
  >(`/patient/${props.patient_id}/assessment/${props.assessment_id}`, {
    skipNotFoundRedirect: true,
  });
  const data = (await response).data;

  return data;
};

export const useAssessmentQuery = (props: IAssessmentProps) => {
  const query = useQuery({
    queryKey: ['assessment', props.patient_id, props.assessment_id],
    enabled: !!props.patient_id && !!props.assessment_id,
    queryFn: () => assessmentRequest(props),
  });

  return query;
};

const patientTablePreferencesRequest = async () => {
  const response = axiosInstance.get<
    paths[`/preferences/patient-table`]['get']['responses']['200']['content']['application/json']
  >(`/preferences/patient-table`);
  const data = (await response).data;

  return data;
};

export const usePatientTablePreferencesQuery = () => {
  const query = useQuery({
    queryKey: ['patient', 'table', 'preferences'],
    queryFn: () => patientTablePreferencesRequest(),
  });

  return query;
};

const assessmentTypesRequest = async () => {
  const response =
    axiosInstance.get<
      paths[`/patient/assessments`]['get']['responses']['200']['content']['application/json']
    >(`/patient/assessments`);
  const data = (await response).data;

  return data;
};

export const useAssessmentTypesQuery = () => {
  const query = useQuery({
    queryKey: ['assessmentTypes'],
    queryFn: () => assessmentTypesRequest(),
  });

  return query;
};

interface IUseAssessmentDatesQueryProps {
  patient_id: string;
}

const assessmentDatesRequest = async ({
  patient_id,
}: IUseAssessmentDatesQueryProps) => {
  const response = axiosInstance.get<
    paths[`/patient/{patient_id}/assessments/dates`]['get']['responses']['200']['content']['application/json']
  >(`/patient/${patient_id}/assessments/dates`);
  const data = (await response).data;

  return data;
};

export const useAssessmentDatesQuery = ({
  patient_id,
}: IUseAssessmentDatesQueryProps) => {
  const query = useQuery({
    queryKey: ['assessments', 'assessmentDates', patient_id],
    queryFn: () => assessmentDatesRequest({ patient_id }),
  });

  return query;
};

interface IExternalAssessmentProps {
  token: string;
  assessment_id: string;
}

const externalAssessmentRequest = async (props: IExternalAssessmentProps) => {
  const response = axiosExternalInstance.get<
    paths[`/assessment/{assessment_id}`]['get']['responses']['200']['content']['application/json']
  >(`/assessment/${props.assessment_id}?token=${props.token}`);
  const data = (await response).data;

  return data;
};

export const useExternalAssessmentQuery = (props: IExternalAssessmentProps) => {
  const query = useQuery({
    queryKey: ['externalAssessment', props.assessment_id, props.token],
    enabled: !!props.token && !!props.assessment_id,
    queryFn: () => externalAssessmentRequest(props),
  });

  return query;
};

const allPatientGoalsRequest = async ({
  patient_id,
  props,
}: {
  props: paths[`/patient/{patient_id}/goals`]['get']['parameters']['query'];
  patient_id: string;
}) => {
  const response = axiosInstance.get<
    paths[`/patient/{patient_id}/goals`]['get']['responses']['200']['content']['application/json']
  >(`/patient/${patient_id}/goals`, { params: props });
  const data = (await response).data;

  return data;
};

export const useAllPatientGoalsQuery = ({
  patient_id,
  props,
}: {
  props: paths[`/patient/{patient_id}/goals`]['get']['parameters']['query'];
  patient_id: string;
}) => {
  const query = useQuery({
    queryKey: [
      'patientsGoals',
      patient_id,
      props?.status_filter,
      props?.sort_order,
    ],
    queryFn: () => allPatientGoalsRequest({ props, patient_id }),
  });

  return query;
};

const careManagementActivitiesRequest = async (patient_id: string) => {
  const response = axiosInstance.get<
    paths[`/patient/{patient_id}/care-management-activities`]['get']['responses']['200']['content']['application/json']
  >(`/patient/${patient_id}/care-management-activities`, {
    params: { timezone },
  });
  const data = (await response).data;

  return data;
};

export const useCareManagementActivitiesQuery = (
  patient_id: string,
  enabled: boolean,
) => {
  const query = useQuery({
    queryKey: ['careManagementActivities', patient_id],
    enabled: !!patient_id && enabled,
    queryFn: () => careManagementActivitiesRequest(patient_id),
  });

  return query;
};
